import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="Terms and conditions for using Lloyd Funding's services and website."
        canonical="/terms-of-service"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Terms of <span className="text-blue-700">Service</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Please read these terms carefully before using our services.
            </p>
            <p className="mt-4 text-sm text-slate-500">
              Last updated: February 2026
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#FFFFFF"
            />
          </svg>
        </div>
      </section>

      {/* Terms of Service Content */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="prose prose-slate prose-lg max-w-none">
            <p className="text-slate-600 leading-relaxed">
              These Terms of Service ("Terms") govern your access to and use of
              the website, mobile applications, products, and services
              (collectively, the "Services") provided by Lloyd Funding Group Inc
              DBA Lloyd Funding ("Lloyd Funding," "we," "us," or "our"). By
              accessing or using our Services, you agree to be bound by these
              Terms. If you do not agree to these Terms, you may not access or
              use our Services.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              1. Eligibility
            </h2>
            <p className="text-slate-600 leading-relaxed">
              To use our Services, you must be at least 18 years of age and have
              the legal capacity to enter into a binding agreement. By using our
              Services, you represent and warrant that you meet these
              eligibility requirements. If you are using our Services on behalf
              of a business, you represent that you have the authority to bind
              that business to these Terms.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              2. Description of Services
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Lloyd Funding provides business funding solutions, including but
              not limited to business lines of credit, term loans, merchant cash
              advances, and debt consolidation services. We act as a facilitator
              connecting businesses with potential lending partners. We are not
              a direct lender, and any funding you receive will be subject to
              the terms and conditions of the respective lending partner.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              3. Application Process
            </h2>
            <p className="text-slate-600 leading-relaxed">
              When you submit an application through our Services, you agree to:
            </p>
            <ul className="text-slate-600 space-y-2">
              <li>
                Provide accurate, current, and complete information about
                yourself and your business
              </li>
              <li>
                Authorize us to verify the information you provide, including
                conducting credit checks and contacting references
              </li>
              <li>
                Authorize us to share your application information with our
                network of lending partners
              </li>
              <li>
                Promptly update any information that changes during the
                application process
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Submitting an application does not guarantee approval or funding.
              All funding decisions are made by our lending partners based on
              their own underwriting criteria.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              4. Credit Authorization
            </h2>
            <p className="text-slate-600 leading-relaxed">
              By submitting an application, you authorize Lloyd Funding and our
              lending partners to obtain your personal and business credit
              reports from one or more credit reporting agencies. You understand
              that this authorization allows for soft credit inquiries during
              the initial application review and may include hard credit
              inquiries if you choose to proceed with a specific funding offer.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              5. Communication Consent
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you consent to receive SMS from Lloyd Funding, you agree to
              receive messages related to your funding application, account
              updates, and promotional offers from us. Reply STOP to opt-out;
              Reply HELP for support or{" "}
              <Link
                to="/contact"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                contact us
              </Link>
              ; Message and data rates may apply; Messaging frequency may vary.
              Visit our{" "}
              <Link
                to="/privacy-policy"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Privacy Policy
              </Link>{" "}
              to see our Privacy Policy and terms and conditions.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By providing your contact information, you also consent to receive
              communications from Lloyd Funding Group Inc DBA Lloyd Funding and
              our partners via telephone and email regarding your application,
              funding options, and related services. You may opt out of
              marketing emails by following the unsubscribe instructions
              provided. Consent to receive text messages is not required for
              application approval.
            </p>
            <p className="text-slate-600 leading-relaxed">
              No mobile opt-in data or text message consent will be shared with
              third parties/affiliates for marketing/promotional purposes. All
              the above categories exclude text messaging originator opt-in data
              and consent; this information will not be shared with any third
              parties.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              6. User Accounts
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you create an account with us, you are responsible for
              maintaining the confidentiality of your account credentials and
              for all activities that occur under your account. You agree to
              notify us immediately of any unauthorized use of your account. We
              reserve the right to suspend or terminate your account at any time
              for any reason, including violation of these Terms.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              7. Prohibited Conduct
            </h2>
            <p className="text-slate-600 leading-relaxed">You agree not to:</p>
            <ul className="text-slate-600 space-y-2">
              <li>
                Provide false, misleading, or fraudulent information in
                connection with your application
              </li>
              <li>Use our Services for any illegal or unauthorized purpose</li>
              <li>
                Interfere with or disrupt the integrity or performance of our
                Services
              </li>
              <li>
                Attempt to gain unauthorized access to our systems or networks
              </li>
              <li>
                Use any automated means to access or collect data from our
                Services
              </li>
              <li>
                Impersonate any person or entity or misrepresent your
                affiliation with any person or entity
              </li>
              <li>
                Violate any applicable laws, regulations, or third-party rights
              </li>
            </ul>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              8. Intellectual Property
            </h2>
            <p className="text-slate-600 leading-relaxed">
              All content, features, and functionality of our Services,
              including but not limited to text, graphics, logos, icons, images,
              and software, are the exclusive property of Lloyd Funding or our
              licensors and are protected by copyright, trademark, and other
              intellectual property laws. You may not reproduce, distribute,
              modify, or create derivative works of any content without our
              prior written consent.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              9. Third-Party Services
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Our Services may contain links to third-party websites or services
              that are not owned or controlled by Lloyd Funding. We have no
              control over and assume no responsibility for the content, privacy
              policies, or practices of any third-party websites or services.
              Your use of third-party services is at your own risk and subject
              to the terms and conditions of those services.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              10. Disclaimer of Warranties
            </h2>
            <p className="text-slate-600 leading-relaxed">
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST
              EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT
              NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
              A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
              OUR SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              11. Limitation of Liability
            </h2>
            <p className="text-slate-600 leading-relaxed">
              TO THE FULLEST EXTENT PERMITTED BY LAW, LLOYD FUNDING AND ITS
              OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
              DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR
              BUSINESS OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF
              OUR SERVICES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF
              SUCH DAMAGES.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              12. Indemnification
            </h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Lloyd Funding
              and its officers, directors, employees, and agents from and
              against any claims, liabilities, damages, losses, and expenses,
              including reasonable attorneys' fees, arising out of or related to
              your use of our Services, your violation of these Terms, or your
              violation of any rights of another party.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              13. Governing Law
            </h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with
              the laws of the State of New York, without regard to its conflict
              of law provisions. Any disputes arising out of or related to these
              Terms or our Services shall be resolved exclusively in the state
              or federal courts located in New York, and you consent to the
              personal jurisdiction of such courts.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              14. Dispute Resolution
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Any dispute, controversy, or claim arising out of or relating to
              these Terms or our Services shall first be attempted to be
              resolved through good faith negotiations. If the dispute cannot be
              resolved through negotiations within 30 days, either party may
              pursue other available legal remedies.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              15. Modifications to Terms
            </h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes
              will be effective immediately upon posting to our website. Your
              continued use of our Services after any changes constitutes your
              acceptance of the modified Terms. We encourage you to review these
              Terms periodically for updates.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              16. Severability
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If any provision of these Terms is found to be invalid or
              unenforceable, the remaining provisions shall continue in full
              force and effect. The invalid or unenforceable provision shall be
              modified to the minimum extent necessary to make it valid and
              enforceable.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              17. Entire Agreement
            </h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the
              entire agreement between you and Lloyd Funding regarding your use
              of our Services and supersede any prior agreements or
              understandings.
            </p>

            <h2 className="mt-10 text-2xl font-bold text-slate-800">
              18. Contact Us
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms of Service, please
              contact us at:
            </p>
            <div className="mt-4 rounded-xl bg-slate-50 p-6">
              <p className="font-semibold text-slate-800">
                Lloyd Funding Group Inc DBA Lloyd Funding
              </p>
              <p className="mt-2 text-slate-600">
                Email:{" "}
                <a
                  href="mailto:submissions@lloydfunding.com"
                  className="text-blue-600 hover:text-blue-700"
                >
                  submissions@lloydfunding.com
                </a>
              </p>
              <p className="text-slate-600">Phone: (786) 321-9346</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-2xl font-bold text-slate-800 sm:text-3xl">
              Ready to get started?
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Apply for funding today and get a decision within hours.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 transition"
              >
                Apply Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-semibold text-blue-600 ring-1 ring-blue-600 hover:bg-blue-50 transition"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TermsOfService;
